import { AuthenticatedDataRequest } from '@eggplant/data-request'
import {
  Combobox,
  DropdownButton,
  NoOptionsPlaceholder
} from '@eggplant/pattern-library'
import { FiPlus } from 'react-icons/fi'
import { HiOutlineDocumentText } from 'react-icons/hi'
import {
  LinkToAddMonitorConfig,
  useAccess
} from '@eggplant/routing'
import {
  TEST_CONFIG,
  TEST_CONFIGURATIONS_NAMES
} from './constants'
import { TEST_CONFIG_SERVICE } from '@eggplant/api-hosts'
import type {
  ButtonShape,
  GetFilterIdListResponse,
  Options
} from '@eggplant/types'

interface Props {
  onChange: (value: string) => void
  placeholder?: string
  value?: string
  shortErrorMessage?: string
  isHighlightedDropdown?: boolean
}

const url = `${TEST_CONFIG_SERVICE}/${TEST_CONFIGURATIONS_NAMES}`

const params = { type: 'monitor' }

const getOptions = (items: GetFilterIdListResponse[] | undefined) => {
  if (!Array.isArray(items)) return []

  return items.map(item => ({
    label: item.name,
    value: item.id_list.join(',')
  }))
}

const SelectModelSuiteMonitorConfigs = ({
  onChange,
  placeholder = 'Monitor config',
  value,
  shortErrorMessage,
  isHighlightedDropdown
}: Props) => {
  const [isAccessible] = useAccess()

  const renderButton = (options: Options) => {
    const currentOption = value
      ? options.find(item => item.value.includes(value))
      : undefined

    const label = currentOption?.value
      ? currentOption.label
      : placeholder

    const testconfigTooltip = [TEST_CONFIG].includes(String(value))
      ? label
      : `Monitor config: ${label}`

    const tooltip = currentOption?.value
      ? testconfigTooltip
      : placeholder

    const buttonText = currentOption?.value
      ? currentOption.label
      : placeholder

    return (buttonProps: ButtonShape) =>
      <DropdownButton
        {...buttonProps}
        Icon={HiOutlineDocumentText}
        isFilter
        isHighlighted={isHighlightedDropdown
          ? !!value
          : false}
        label={label}
        title={tooltip}
      >
        {buttonText}
      </DropdownButton>
  }

  const noDataPlaceholder =
    <NoOptionsPlaceholder
      icon={<HiOutlineDocumentText size='24px' />}
      linkTo={
        isAccessible
          ? (
            <LinkToAddMonitorConfig>
              <FiPlus
                aria-hidden
                size={16}
              />
              New Monitor Config
            </LinkToAddMonitorConfig>
          )
          : undefined
      }
      title='No monitor config found'
    />

  return (
    <div className='flex'>
      <AuthenticatedDataRequest
        method='get'
        params={params}
        shortErrorMessage={shortErrorMessage || 'Failed to load Monitor config'}
        url={url}
      >
        {
          (data: GetFilterIdListResponse[]) =>
            <Combobox
              isFilter
              noOptionsPlaceholder={noDataPlaceholder}
              onChange={onChange}
              options={getOptions(data)}
              renderButton={renderButton(getOptions(data))}
              value={value}
            />
        }
      </AuthenticatedDataRequest>
    </div>
  )
}

export default SelectModelSuiteMonitorConfigs
